import { Button, Form, Input, Modal, Row, Space, Switch, Table, Tooltip, Typography } from 'antd'
import { editGreySVG } from 'assets/svg/edit'
import { QuestionIcon } from 'assets/svg/questionMark'
import { SelectionInfoContainer } from 'atom/box'
import { ButtonModal } from 'atom/button'
import { EditButtonWrapper } from 'atom/users-page-redesign-styles'
import { CancelModal } from 'components/modal/CancelModal'
import { useGeneralTranslation } from 'hooks/translation/useGeneralTranslation'
import { useNotifications } from 'hooks/useNotification'
import { useEffect, useState } from 'react'
import { CreateUserRFID, DeleteUserRFIDs, GetUserRFID, UpdateUserRFID } from 'services/data-provider/users'
import { useAppState } from 'state'
import { ButtonTextSpacing, FooterLayout, Heading } from '../../../atom/user-edit'
import { useUserTranslation } from '../../../hooks/translation/useUserTranslation'
import { SwtchError } from '../../../models/error'
import { formatRfid } from '../../../helpers/formatRfid'
import { AugmentedUser, RFID, RFIDRequest } from '../../../models/user'

interface props {
  selectedUser: AugmentedUser
  onCancel: () => void
}

export const UserRFIDForm: React.FC<props> = ({ onCancel, selectedUser }) => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const { saveText, updateText } = useUserTranslation()
  const { openSuccessNotification, openErrorNotification } = useNotifications()
  const { isMockUpEnabled, currentUser } = useAppState()
  const isAdmin = currentUser?.role === 'admin'

  const afterClose = () => {
    form.resetFields()
  }
  const {
    rfidTitleText,
    rfidUserText,
    rfidAddRFIDText,
    rfidAddRFIDPlaceholderText,
    rfidAddText,
    rfidAssignedRFIDText,
    rfidRemoveRFIDText,
    rfidRemoveRFIDWarningText,
    rfidRemoveMultipleRFIDText,
    rfidRFIDremovedText,
    rfidSelectedText,
    rfidRFIDaddedText,
    rfidNicknameText,
  } = useUserTranslation()

  const { removeText, cancelText, somethingWentWrongText } = useGeneralTranslation()

  const [showRemoveRFIDModal, setShowRemoveRFIDModal] = useState(false)

  const [rfidsToRemove, setRfidsToRemove] = useState<RFID[]>([])
  const [rfidsToModify, setRfidsToModify] = useState<RFID[]>([])

  const [addRFIDError, setAddRFIDError] = useState('')
  const [editRFIDError, setEditRFIDError] = useState<{ [id: string]: string }>({})

  const [editingRFID, setEditingRFID] = useState<{
    [id: string]:
      | {
          rfidNumber: string
          nickName?: string
          invoicePaymentEnabled?: boolean
          invoicePaymentEmail?: string
        }
      | undefined
  }>({})

  const [savedEdits, setSavedEdits] = useState<{
    [id: string]:
      | {
          rfidNumber: string
          nickName?: string
          invoicePaymentEnabled?: boolean
          invoicePaymentEmail?: string
        }
      | undefined
  }>({})

  const [showEditRFID, setShowEditRFID] = useState<{
    [id: string]: boolean
  }>({})
  const [editInvoiceEmailError, setEditInvoiceEmailError] = useState<{ [id: string]: string }>({})
  const [rfids, setRfids] = useState<RFID[]>([])
  const [rfidSearch, setRfidSearch] = useState('')
  const invoicePaymentTooltipText =
    'Indicates the RFID transaction will not be charged immediately but will be billed at the end of the month'

  const disableSave =
    !Object.values(savedEdits).some((rfid) => rfid !== undefined) || Object.values(showEditRFID).some((rfid) => rfid)

  useEffect(() => {
    setRfidsToRemove(rfidsToModify)
  }, [rfidsToModify])

  useEffect(() => {
    if (selectedUser) {
      getRFID()
    }
  }, [selectedUser])

  const formatRFID = (rfid: string) => formatRfid(rfid)

  const handleRFIDChange = (record: RFID, selected: boolean) => {
    if (selected) {
      setRfidsToModify([...rfidsToModify, record])
    } else {
      setRfidsToModify(rfidsToModify.filter((r) => r.id !== record.id))
    }
  }

  const handleRemoveRFIDModal = () => setShowRemoveRFIDModal(!showRemoveRFIDModal)

  const cancelModifyRFID = () => setRfidsToModify([])

  const handleShowRFIDEdit = (id: number) => () => {
    const isCurrentlyEditing = showEditRFID[id]

    if (!isCurrentlyEditing) {
      // Entering edit mode - initialize with current values (saved edits or original)
      const currentRFID = rfids.find((rfid) => rfid.id === id)
      if (currentRFID) {
        setEditingRFID({
          ...editingRFID,
          [id]: {
            rfidNumber: savedEdits[id]?.rfidNumber || currentRFID.rfidNumber,
            nickName: savedEdits[id]?.nickName !== undefined ? savedEdits[id]?.nickName : currentRFID.nickName || '',
            invoicePaymentEnabled:
              savedEdits[id]?.invoicePaymentEnabled !== undefined
                ? savedEdits[id]?.invoicePaymentEnabled
                : !!currentRFID.invoicePaymentEnabled,
            invoicePaymentEmail:
              savedEdits[id]?.invoicePaymentEmail !== undefined
                ? savedEdits[id]?.invoicePaymentEmail
                : currentRFID.invoicePaymentEmail || '',
          },
        })
      }
    }

    setShowEditRFID({ ...showEditRFID, [id]: !isCurrentlyEditing })
  }

  const handleRFIDEdit = (id: number, field: 'rfidNumber' | 'nickName' | 'invoicePaymentEmail') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    if (field === 'invoicePaymentEmail') {
      setEditInvoiceEmailError({ ...editInvoiceEmailError, [id]: '' })
    }

    setEditingRFID({
      ...editingRFID,
      [id]: {
        rfidNumber: field === 'rfidNumber' ? formatRfid(raw) : editingRFID[id]?.rfidNumber || '',
        nickName: field === 'nickName' ? raw : editingRFID[id]?.nickName || '',
        invoicePaymentEnabled: editingRFID[id]?.invoicePaymentEnabled ?? false,
        invoicePaymentEmail: field === 'invoicePaymentEmail' ? raw : editingRFID[id]?.invoicePaymentEmail || '',
      },
    })
  }

  const handleInvoicePaymentToggle = (id: number) => (checked: boolean) => {
    setEditInvoiceEmailError({ ...editInvoiceEmailError, [id]: '' })
    setEditingRFID({
      ...editingRFID,
      [id]: {
        rfidNumber: editingRFID[id]?.rfidNumber || '',
        nickName: editingRFID[id]?.nickName || '',
        invoicePaymentEnabled: checked,
        invoicePaymentEmail: checked ? editingRFID[id]?.invoicePaymentEmail || '' : '',
      },
    })
  }

  const cancelRFIDEdit = (id: number) => () => {
    // Revert to saved edits or original values
    const currentRFID = rfids.find((rfid) => rfid.id === id)
    if (currentRFID) {
      setEditingRFID({
        ...editingRFID,
        [id]: {
          rfidNumber: savedEdits[id]?.rfidNumber || currentRFID.rfidNumber,
          nickName: savedEdits[id]?.nickName !== undefined ? savedEdits[id]?.nickName : currentRFID.nickName || '',
          invoicePaymentEnabled:
            savedEdits[id]?.invoicePaymentEnabled !== undefined
              ? savedEdits[id]?.invoicePaymentEnabled
              : !!currentRFID.invoicePaymentEnabled,
          invoicePaymentEmail:
            savedEdits[id]?.invoicePaymentEmail !== undefined
              ? savedEdits[id]?.invoicePaymentEmail
              : currentRFID.invoicePaymentEmail || '',
        },
      })
    }

    setShowEditRFID({ ...showEditRFID, [id]: false })
    setEditRFIDError({ ...editRFIDError, [id]: undefined })
    setEditInvoiceEmailError({ ...editInvoiceEmailError, [id]: '' })
  }

  const saveRFIDEdit = (id: number) => () => {
    const invoicePaymentEnabled = !!editingRFID[id]?.invoicePaymentEnabled
    const invoicePaymentEmail = (editingRFID[id]?.invoicePaymentEmail || '').trim()

    if (invoicePaymentEnabled && !invoicePaymentEmail) {
      setEditInvoiceEmailError({
        ...editInvoiceEmailError,
        [id]: 'Invoice email is required when Invoice Payment is enabled.',
      })
      return
    }

    if (invoicePaymentEnabled && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(invoicePaymentEmail)) {
      setEditInvoiceEmailError({
        ...editInvoiceEmailError,
        [id]: 'Please enter a valid email address.',
      })
      return
    }

    // Save the current edits to savedEdits state
    setSavedEdits({
      ...savedEdits,
      [id]: {
        rfidNumber: formatRFID(editingRFID[id]?.rfidNumber || ''),
        nickName: editingRFID[id]?.nickName || '',
        invoicePaymentEnabled,
        invoicePaymentEmail: invoicePaymentEnabled ? invoicePaymentEmail : '',
      },
    })

    setShowEditRFID({ ...showEditRFID, [id]: false })
    setEditInvoiceEmailError({ ...editInvoiceEmailError, [id]: '' })
  }

  const getRFID = async () => {
    try {
      setLoading(true)
      const rfids = await GetUserRFID(selectedUser.id, isMockUpEnabled)
      setRfids(rfids)
    } catch (error) {
      const errorMessage = (error as SwtchError)?.cause?.message || somethingWentWrongText
      openErrorNotification(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const updateRFID = async () => {
    try {
      setLoading(true)
      const response = await UpdateUserRFID(
        selectedUser.id,
        Object.entries(savedEdits)
          .map(([id, rfidData]) => ({
            id: Number(id),
            rfid_number: rfidData?.rfidNumber || '',
            nick_name: rfidData?.nickName || '',
            invoicePaymentEnabled: rfidData?.invoicePaymentEnabled ?? false,
            invoicePaymentEmail: rfidData?.invoicePaymentEmail || '',
          }))
          .filter(({ rfid_number }) => rfid_number !== '') as RFIDRequest[],
        isMockUpEnabled,
      )
      openSuccessNotification(response.message)
      onCancel()
    } catch (error) {
      const errorMessage = (error as SwtchError)?.cause?.message || ''
      const errorMessages = errorMessage.split(', ')
      const regex = /RFID (.*?): /

      let showEditRFIDWithErrors: { [id: string]: boolean } = {}
      let editingRFIDWithErrors: { [id: string]: string } = {}

      errorMessages.forEach((error) => {
        const match = error.match(regex)
        const rfidNumber = match?.[1]
        const rfidId = Object.keys(savedEdits).find((id) => savedEdits[id]?.rfidNumber === rfidNumber)
        if (rfidNumber && rfidId) {
          showEditRFIDWithErrors[rfidId] = true
          editingRFIDWithErrors[rfidId] = error
        }
      })

      setRfids(
        rfids.map((rfid) => ({
          ...rfid,
          rfidNumber: !editingRFIDWithErrors[rfid.id]
            ? savedEdits[rfid.id]?.rfidNumber || rfid.rfidNumber
            : rfid.rfidNumber,
        })),
      )

      setShowEditRFID(showEditRFIDWithErrors)
      setEditRFIDError(editingRFIDWithErrors)
    } finally {
      setLoading(false)
    }
  }

  const removeRFIDs = async () => {
    try {
      setLoading(true)
      await DeleteUserRFIDs(
        selectedUser.id,
        rfidsToRemove.map(({ id }) => id),
        isMockUpEnabled,
      )
      openSuccessNotification(`${rfidsToRemove.length} ${rfidRFIDremovedText}`)
    } catch (error) {
      const errorMessage = (error as SwtchError)?.cause?.message || somethingWentWrongText
      openErrorNotification(errorMessage)
    } finally {
      setLoading(false)
      setRfidsToRemove([])
      setRfidsToModify([])
      handleRemoveRFIDModal()
      await getRFID()
    }
  }

  const addRFIDs = async () => {
    try {
      const rfidsToAdd = Array.from(new Set(rfidSearch.split(',').map((rfid) => formatRFID(rfid)).filter((rfid) => !!rfid)))
      await CreateUserRFID(
        selectedUser.id,
        rfidsToAdd.map((rfid_number) => ({ rfid_number })),
        isMockUpEnabled,
      )
      openSuccessNotification(`${rfidsToAdd.length} ${rfidRFIDaddedText}`)
      setRfidSearch('')
    } catch (error) {
      const errorMessage = (error as SwtchError)?.cause?.message || somethingWentWrongText
      setAddRFIDError(errorMessage)
    } finally {
      await getRFID()
    }
  }

  const columns = [
    {
      title: 'RFID',
      width: 170,
      className: 'ellipsis-column',
      key: 'rfid',
      sorter: (a: any, b: any) => a.rfidNumber.localeCompare(b.rfidNumber),
      render: (record: RFID) =>
        showEditRFID[record.id] && !rfidsToModify.length ? (
          <Form.Item
            validateStatus={editRFIDError[record.id] ? 'error' : ''}
            help={editRFIDError[record.id] ?? undefined}
            className="editable-input-inside-table"
          >
            <Input
              value={editingRFID[record.id]?.rfidNumber || record.rfidNumber}
              onChange={handleRFIDEdit(record.id, 'rfidNumber')}
            />
          </Form.Item>
        ) : (
          <Typography.Text ellipsis={{ tooltip: savedEdits[record.id]?.rfidNumber || record.rfidNumber || '' }}>
            {savedEdits[record.id]?.rfidNumber || record.rfidNumber || ''}
          </Typography.Text>
        ),
    },
    {
      title: rfidNicknameText,
      width: 150,
      className: 'ellipsis-column',
      key: 'nickName',
      sorter: (a: any, b: any) => (a.nickName || '').localeCompare(b.nickName || ''),
      render: (record: RFID) =>
        showEditRFID[record.id] && !rfidsToModify.length ? (
          <Form.Item
            validateStatus={editRFIDError[record.id] ? 'error' : ''}
            help={editRFIDError[record.id] ?? undefined}
            className="editable-input-inside-table"
          >
            <Input
              maxLength={50}
              value={
                editingRFID[record.id]?.nickName !== undefined
                  ? editingRFID[record.id]?.nickName
                  : record.nickName || ''
              }
              onChange={handleRFIDEdit(record.id, 'nickName')}
            />
          </Form.Item>
        ) : (
          <Typography.Text ellipsis={{ tooltip: savedEdits[record.id]?.nickName || record.nickName || '' }}>
            {savedEdits[record.id]?.nickName !== undefined ? savedEdits[record.id]?.nickName : record.nickName || ''}
          </Typography.Text>
        ),
    },
    ...(isAdmin
      ? [
          {
            title: (
              <Row align="middle" style={{ gap: 8, flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
                <span>Invoice Payment</span>
                <Tooltip title={invoicePaymentTooltipText}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <QuestionIcon />
                  </div>
                </Tooltip>
              </Row>
            ),
            width: 140,
            key: 'invoicePayment',
            render: (record: RFID) =>
              showEditRFID[record.id] && !rfidsToModify.length ? (
                <Switch
                  checked={
                    editingRFID[record.id]?.invoicePaymentEnabled ??
                    savedEdits[record.id]?.invoicePaymentEnabled ??
                    !!record.invoicePaymentEnabled
                  }
                  onChange={handleInvoicePaymentToggle(record.id)}
                />
              ) : (
                <Switch checked={savedEdits[record.id]?.invoicePaymentEnabled ?? !!record.invoicePaymentEnabled} disabled />
              ),
          },
          {
            title: 'Invoice Email',
            width: 210,
            key: 'invoiceEmail',
            render: (record: RFID) =>
              showEditRFID[record.id] && !rfidsToModify.length ? (
                <Form.Item
                  validateStatus={editInvoiceEmailError[record.id] ? 'error' : ''}
                  help={editInvoiceEmailError[record.id] || undefined}
                  className="editable-input-inside-table"
                >
                  <Input
                    value={
                      editingRFID[record.id]?.invoicePaymentEmail !== undefined
                        ? editingRFID[record.id]?.invoicePaymentEmail
                        : savedEdits[record.id]?.invoicePaymentEmail !== undefined
                          ? savedEdits[record.id]?.invoicePaymentEmail
                          : record.invoicePaymentEmail || ''
                    }
                    onChange={handleRFIDEdit(record.id, 'invoicePaymentEmail')}
                    placeholder="email@example.com"
                    disabled={
                      !(
                        editingRFID[record.id]?.invoicePaymentEnabled ??
                        savedEdits[record.id]?.invoicePaymentEnabled ??
                        !!record.invoicePaymentEnabled
                      )
                    }
                  />
                </Form.Item>
              ) : (
                <Typography.Text ellipsis={{ tooltip: savedEdits[record.id]?.invoicePaymentEmail || record.invoicePaymentEmail || '' }}>
                  {(savedEdits[record.id]?.invoicePaymentEnabled ?? !!record.invoicePaymentEnabled)
                    ? savedEdits[record.id]?.invoicePaymentEmail || record.invoicePaymentEmail || '—'
                    : '—'}
                </Typography.Text>
              ),
          },
        ]
      : []),
    {
      title: <div style={{ paddingRight: 8 }}>Action</div>,
      align: 'right' as const,
      width: 190,
      key: 'action',
      onHeaderCell: () => ({
        style: { paddingRight: 24 },
      }),
      onCell: () => ({
        style: { paddingRight: 24 },
      }),
      render: (record: RFID) => (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <EditButtonWrapper style={{ gap: '10px', whiteSpace: 'nowrap' }} className={rfidsToModify.length ? 'disable-action-button' : ''}>
            {showEditRFID[record.id] ? (
              <>
                <Button type="primary" onClick={saveRFIDEdit(record.id)} style={{ marginLeft: '12px' }}>
                  {saveText}
                </Button>
                <Button type="ghost" className="show-button-border" onClick={cancelRFIDEdit(record.id)}>
                  {cancelText}
                </Button>
              </>
            ) : (
              <>
                <ButtonModal
                  type="link"
                  icon={editGreySVG}
                  disabled={!!rfidsToModify.length || showEditRFID[record.id]}
                  click={handleShowRFIDEdit(record.id)}
                  className={`edit-button ${showEditRFID[record.id] ? 'disable-action-button' : ''}`}
                />
              </>
            )}
          </EditButtonWrapper>
        </div>
      ),
    },
  ]

  return (
    <Modal
      title={<Heading style={{ fontSize: 14 }}>{rfidTitleText}</Heading>}
      closable={true}
      okText={updateText}
      visible={true}
      onCancel={onCancel}
      afterClose={afterClose}
      destroyOnClose
      width={960}
      className="fixed-row-modal default-modal max-height-modal rfid-modal"
      footer={
        <FooterLayout justifyContent="flex-end" style={{ paddingTop: 4, paddingBottom: 4 }}>
          <Button
            style={{ width: '140px' }}
            type="primary"
            loading={loading}
            onClick={updateRFID}
            disabled={disableSave}
          >
            <ButtonTextSpacing disabled={disableSave}>
              <span className="paragraph-02-regular">{saveText}</span>
            </ButtonTextSpacing>
          </Button>
        </FooterLayout>
      }
    >
      <CancelModal
        visible={showRemoveRFIDModal}
        title={
          rfidsToRemove.length > 1
            ? `${rfidRemoveMultipleRFIDText.replace('{count}', rfidsToRemove.length.toString())}?`
            : `${rfidRemoveRFIDText} ${rfidsToRemove[0]?.rfidNumber}?`
        }
        description={rfidRemoveRFIDWarningText}
        onCancel={handleRemoveRFIDModal}
        onSubmit={removeRFIDs}
      />
      <Form form={form} layout="vertical" initialValues={{ email: selectedUser.email }}>
        <Form.Item
          name="email"
          className="disabled-placeholder-clear"
          label={<span className="heading-06-regular">{rfidUserText}</span>}
        >
          <Input disabled />
        </Form.Item>
        <Form.Item
          validateStatus={addRFIDError ? 'error' : ''}
          help={addRFIDError ? addRFIDError.split(', ').map((error, i) => <div key={i}>{error}</div>) : undefined}
          label={<span className="heading-06-regular">{rfidAddRFIDText}</span>}
        >
          <Row style={{ gap: '10px' }}>
            <div style={{ flex: 1, maxWidth: 'calc(100% - 79px)' }}>
              <Input
                placeholder={rfidAddRFIDPlaceholderText}
                value={rfidSearch}
                onChange={(e) => {
                  setAddRFIDError('')
                  setRfidSearch(e.target.value)
                }}
                onPressEnter={addRFIDs}
              />
            </div>
            <Button type="primary" onClick={addRFIDs} disabled={!rfidSearch.trim()}>
              <span className="paragraph-02-regular">{rfidAddText}</span>
            </Button>
          </Row>
        </Form.Item>
        {!!rfidsToModify.length && (
          <SelectionInfoContainer align="middle" justify="space-between" style={{ display: loading ? 'none' : 'flex' }}>
            <Space>
              <span className="paragraph-01-regular">{`${rfidsToModify.length} ${rfidSelectedText}`}</span>
            </Space>
            <Space>
              <Button type="ghost" onClick={cancelModifyRFID}>
                {cancelText}
              </Button>
              <Button type="primary" onClick={handleRemoveRFIDModal}>
                {removeText}
              </Button>
            </Space>
          </SelectionInfoContainer>
        )}
        <Form.Item label={<span className="heading-06-regular">{`${rfids.length} ${rfidAssignedRFIDText}`}</span>}>
          {!!rfids.length && (
            <Table
              loading={loading}
              rowKey="id"
              className="sort-icon-close shadow-table shrink-row action-with-no-border editable-input"
              tableLayout="fixed"
              columns={columns}
              dataSource={rfids}
              rowSelection={{
                selectedRowKeys: rfidsToModify.map((r) => r.id),
                onSelect: handleRFIDChange,
                onChange: (_, rows) => setRfidsToModify(rows),
              }}
              pagination={false}
              style={{ marginBottom: '2rem' }} // to give space between tenants
            />
          )}
        </Form.Item>
      </Form>
    </Modal>
  )
}
